import React from "react";
import type { WidgetProps, WidgetState } from "../../BaseWidget";
import BaseWidget from "../../BaseWidget";
import type { WidgetType } from "constants/WidgetConstants";
import { EventType } from "constants/AppsmithActionConstants/ActionConstants";
import DatePickerComponent from "../component";
import type { ValidationResponse } from "constants/WidgetValidation";
import { ISO_DATE_FORMAT, ValidationTypes } from "constants/WidgetValidation";
import type { DerivedPropertiesMap } from "utils/WidgetFactory";
import moment from "moment";
import type { DatePickerType } from "../constants";
import { AutocompleteDataType } from "utils/autocomplete/AutocompleteDataType";
import { DefaultAutocompleteDefinitions } from "widgets/WidgetUtils";
import type { AutocompletionDefinitions } from "widgets/constants";

function defaultDateValidation(
  value: unknown,
  props: DatePickerWidgetProps,
  _?: any,
  moment?: any,
): ValidationResponse {
  const dateFormat = props.dateFormat || ISO_DATE_FORMAT;
  if (value === null) {
    return {
      isValid: true,
      parsed: "",
      messages: [{ name: "", message: "" }],
    };
  }
  if (value === undefined) {
    return {
      isValid: false,
      parsed: "",
      messages: [
        {
          name: "TypeError",
          message: `This value does not evaluate to type: Date ${dateFormat}`,
        },
      ],
    };
  }

  const isValid = moment(value as string, dateFormat).isValid();

  return {
    isValid,
    parsed: isValid ? value : "",
    messages:
      isValid === false
        ? [
            {
              name: "TypeError",
              message: `Value does not match ISO 8601 standard date string`,
            },
          ]
        : [{ name: "", message: "" }],
  };
}

function minDateValidation(
  value: unknown,
  props: DatePickerWidgetProps,
  _?: any,
  moment?: any,
): ValidationResponse {
  const dateFormat = props.dateFormat || ISO_DATE_FORMAT;
  if (value === undefined) {
    return {
      isValid: false,
      parsed: "",
      messages: [
        {
          name: "TypeError",
          message:
            `Value does not match: Date String ` +
            (dateFormat ? dateFormat : ""),
        },
      ],
    };
  }
  const parsedMinDate = moment(value as string, dateFormat);
  let isValid = parsedMinDate.isValid();

  if (!props.defaultDate) {
    return {
      isValid: isValid,
      parsed: value,
      messages: [{ name: "", message: "" }],
    };
  }
  const parsedDefaultDate = moment(props.defaultDate, dateFormat);

  if (
    isValid &&
    parsedDefaultDate.isValid() &&
    parsedDefaultDate.isBefore(parsedMinDate)
  ) {
    isValid = false;
  }
  if (!isValid) {
    return {
      isValid: isValid,
      parsed: "",
      messages: [
        {
          name: "TypeError",
          message:
            `Value does not match: Date String ` +
            (dateFormat ? dateFormat : ""),
        },
      ],
    };
  }
  return {
    isValid: isValid,
    parsed: value,
    messages: [{ name: "", message: "" }],
  };
}

function maxDateValidation(
  value: unknown,
  props: DatePickerWidgetProps,
  _?: any,
  moment?: any,
): ValidationResponse {
  const dateFormat = props.dateFormat || ISO_DATE_FORMAT;
  if (value === undefined) {
    return {
      isValid: false,
      parsed: "",
      messages: [
        {
          name: "TypeError",
          message:
            `Value does not match type: Date String ` +
            (dateFormat ? dateFormat : ""),
        },
      ],
    };
  }
  const parsedMaxDate = moment(value as string, dateFormat);
  let isValid = parsedMaxDate.isValid();
  if (!props.defaultDate) {
    return {
      isValid: isValid,
      parsed: value,
      messages: [{ name: "", message: "" }],
    };
  }
  const parsedDefaultDate = moment(props.defaultDate, dateFormat);

  if (
    isValid &&
    parsedDefaultDate.isValid() &&
    parsedDefaultDate.isAfter(parsedMaxDate)
  ) {
    isValid = false;
  }
  if (!isValid) {
    return {
      isValid: isValid,
      parsed: "",
      messages: [
        {
          name: "TypeError",
          message:
            `Value does not match type: Date String ` +
            (dateFormat ? dateFormat : ""),
        },
      ],
    };
  }
  return {
    isValid: isValid,
    parsed: value,
    messages: [{ name: "", message: "" }],
  };
}
class DatePickerWidget extends BaseWidget<DatePickerWidgetProps, WidgetState> {
  static getAutocompleteDefinitions(): AutocompletionDefinitions {
    return {
      "!doc":
        "Datepicker is used to capture the date and time from a user. It can be used to filter data base on the input date range as well as to capture personal information such as date of birth",
      "!url": "https://docs.appsmith.com/widget-reference/datepicker",
      isVisible: DefaultAutocompleteDefinitions.isVisible,
      selectedDate: "string",
      isDisabled: "bool",
    };
  }

  static getPropertyPaneConfig() {
    return [
      {
        sectionName: "一般",
        children: [
          {
            propertyName: "defaultDate",
            label: "默认日期",
            helpText:
              "Sets the default date of the widget. The date is updated if the default date changes",
            controlType: "DATE_PICKER",
            isJSConvertible: true,
            isBindProperty: true,
            isTriggerProperty: false,
            validation: {
              type: ValidationTypes.FUNCTION,
              params: {
                fn: defaultDateValidation,
                expected: {
                  type: "ISO 8601 string",
                  example: moment().toISOString(),
                  autocompleteDataType: AutocompleteDataType.STRING,
                },
              },
            },
            dependencies: ["dateFormat"],
          },
          {
            helpText: "Sets the format of the selected date",
            propertyName: "dateFormat",
            label: "日期格式",
            controlType: "DROP_DOWN",
            isJSConvertible: true,
            options: [
              {
                label: "YYYY-MM-DD",
                value: "YYYY-MM-DD",
              },
              {
                label: "YYYY-MM-DD HH:mm",
                value: "YYYY-MM-DD HH:mm",
              },
              {
                label: "YYYY-MM-DDTHH:mm:ss.sssZ",
                value: "YYYY-MM-DDTHH:mm:ss.sssZ",
              },
              {
                label: "DD/MM/YYYY",
                value: "DD/MM/YYYY",
              },
              {
                label: "DD/MM/YYYY HH:mm",
                value: "DD/MM/YYYY HH:mm",
              },
              {
                label: "MM/DD/YYYY",
                value: "MM/DD/YYYY",
              },
              {
                label: "MM/DD/YYYY HH:mm",
                value: "MM/DD/YYYY HH:mm",
              },
              {
                label: "MM/DD/YYYY HH:mm:ss",
                value: "MM/DD/YYYY HH:mm:ss",
              },
            ],
            isBindProperty: true,
            isTriggerProperty: false,
            validation: { type: ValidationTypes.TEXT },
          },
          {
            propertyName: "isRequired",
            label: "是否必填",
            helpText: "Makes input to the widget mandatory",
            controlType: "SWITCH",
            isJSConvertible: true,
            isBindProperty: true,
            isTriggerProperty: false,
            validation: { type: ValidationTypes.BOOLEAN },
          },
          {
            propertyName: "isVisible",
            label: "是否可见",
            helpText: "Controls the visibility of the widget",
            controlType: "SWITCH",
            isJSConvertible: true,
            isBindProperty: true,
            isTriggerProperty: false,
            validation: { type: ValidationTypes.BOOLEAN },
          },
          {
            propertyName: "isDisabled",
            label: "是否禁用",
            helpText: "Disables input to this widget",
            controlType: "SWITCH",
            isJSConvertible: true,
            isBindProperty: true,
            isTriggerProperty: false,
            validation: { type: ValidationTypes.BOOLEAN },
          },
          {
            propertyName: "animateLoading",
            label: "加载动画",
            controlType: "SWITCH",
            helpText: "Controls the loading of the widget",
            defaultValue: true,
            isJSConvertible: true,
            isBindProperty: true,
            isTriggerProperty: false,
            validation: { type: ValidationTypes.BOOLEAN },
          },
          {
            propertyName: "minDate",
            label: "最小日期",
            helpText: "Defines the min date for this widget",
            controlType: "DATE_PICKER",
            isJSConvertible: true,
            isBindProperty: true,
            isTriggerProperty: false,
            validation: {
              type: ValidationTypes.FUNCTION,
              params: {
                fn: minDateValidation,
                expected: {
                  type: "ISO 8601 string",
                  example: moment().toISOString(),
                  autocompleteDataType: AutocompleteDataType.STRING,
                },
              },
            },
            dependencies: ["dateFormat", "defaultDate"],
          },
          {
            propertyName: "maxDate",
            label: "最大日期",
            helpText: "Defines the max date for this widget",
            controlType: "DATE_PICKER",
            isJSConvertible: true,
            isBindProperty: true,
            isTriggerProperty: false,
            validation: {
              type: ValidationTypes.FUNCTION,
              params: {
                fn: maxDateValidation,
                expected: {
                  type: "ISO 8601 string",
                  example: moment().toISOString(),
                  autocompleteDataType: AutocompleteDataType.STRING,
                },
              },
            },
            dependencies: ["dateFormat", "defaultDate"],
          },
        ],
      },
      {
        sectionName: "Events",
        children: [
          {
            propertyName: "onDateSelected",
            label: "onDateSelected",
            controlType: "ACTION_SELECTOR",
            isJSConvertible: true,
            isBindProperty: true,
            isTriggerProperty: true,
          },
        ],
      },
    ];
  }

  static getDerivedPropertiesMap(): DerivedPropertiesMap {
    return {
      isValid: `{{ this.isRequired ? !!this.selectedDate : true }}`,
      value: `{{ this.selectedDate }}`,
    };
  }

  static getDefaultPropertiesMap(): Record<string, string> {
    return {
      selectedDate: "defaultDate",
    };
  }

  static getMetaPropertiesMap(): Record<string, any> {
    return {
      selectedDate: undefined,
    };
  }

  componentDidUpdate(prevProps: DatePickerWidgetProps) {
    if (this.props.dateFormat !== prevProps.dateFormat) {
      if (this.props.defaultDate) {
        const defaultDate = moment(
          this.props.defaultDate,
          this.props.dateFormat,
        );
        if (!defaultDate.isValid()) {
          super.updateWidgetProperty("defaultDate", "");
        } else {
          if (this.props.minDate) {
            const minDate = moment(this.props.minDate, this.props.dateFormat);
            if (!minDate.isValid() || defaultDate.isBefore(minDate)) {
              super.updateWidgetProperty("defaultDate", "");
            }
          }
          if (this.props.maxDate) {
            const maxDate = moment(this.props.maxDate, this.props.dateFormat);
            if (!maxDate.isValid() || defaultDate.isAfter(maxDate)) {
              super.updateWidgetProperty("defaultDate", "");
            }
          }
        }
      }
    }
  }

  getPageView() {
    return (
      <DatePickerComponent
        dateFormat={this.props.dateFormat}
        datePickerType={"DATE_PICKER"}
        isDisabled={this.props.isDisabled}
        isLoading={this.props.isLoading}
        label={`${this.props.label}`}
        maxDate={this.props.maxDate}
        minDate={this.props.minDate}
        onDateSelected={this.onDateSelected}
        selectedDate={this.props.selectedDate}
        widgetId={this.props.widgetId}
      />
    );
  }

  onDateSelected = (selectedDate: string) => {
    this.props.updateWidgetMetaProperty("selectedDate", selectedDate, {
      triggerPropertyName: "onDateSelected",
      dynamicString: this.props.onDateSelected,
      event: {
        type: EventType.ON_DATE_SELECTED,
      },
    });
  };

  static getWidgetType(): WidgetType {
    return "DATE_PICKER_WIDGET";
  }
}

export interface DatePickerWidgetProps extends WidgetProps {
  defaultDate: string;
  selectedDate: string;
  isDisabled: boolean;
  dateFormat: string;
  label: string;
  datePickerType: DatePickerType;
  onDateSelected?: string;
  onDateRangeSelected?: string;
  maxDate: string;
  minDate: string;
  isRequired?: boolean;
}

export default DatePickerWidget;
